import { defineStore } from 'pinia';
import { Menu, Constants } from '@fibl/oxs-common';
import type { EAV, Search } from '@fibl/zdb-common';
import * as SearchRepo from '@/repository/search';
import { useWebsiteStore } from './website';
import { useSelectOptionStore } from './selectoption';

type Ent = EAV.Entity<Menu.Menu>;

export const useMenuStore = defineStore('menu', {
	state: () => ({
		menus: [] as Ent[],
		pseudoMenu: null as Ent|null,
	}),

	getters: {
		byId() {
			let idx = new Map(this.menus.map((e) => [e.id, e]));
			if (this.pseudoMenu) idx.set(this.pseudoMenu.id, this.pseudoMenu);
			return (id: string): Ent => {
				return idx.get(id)!;
			};
		},
		getChildren() {
			return (parentId: string): Ent[] => {
				return this.menus.filter((e) => e.data.parent === parentId);
			};
		},
		getChildrenRecursive() {
			return (id: string): Ent[] => {
				const res: Ent[] = [];
				this.getChildren(id).forEach(ent => {
					res.push(ent);
					let sub = this.getChildrenRecursive(ent.id);
					sub.forEach(s => res.push(s));
				});
				return res;
			};
		}
	},

	actions: {
		async loadForWebsite() {
			const country = useWebsiteStore().countryCode;
			const website = useWebsiteStore().websiteCode;
			let filter: SearchRepo.SearchFilter = { $and: [] };
			filter.$and.push({
				field: '/data/website',
				comparison: 'eq',
				value: website,
			});
			if (website === Constants.WEB_OXS) {
				filter.$and.push({
					field: '/data/country',
					comparison: 'eq',
					value: country,
				});
			}
			this.menus = await SearchRepo.findAll('menu', filter, true);
		},
		getSpeciesFilter(menu: Ent, forRequest: boolean): Search.SearchFilter {
			const country = useWebsiteStore().countryCode;
			const website = useWebsiteStore().websiteCode;
			const filter: Search.SearchFilter = { $and: [] };
			if (menu.data.search.species.length) {
				filter.$and.push({
					$or: menu.data.search.species.map(id =>({
						field: '/id',
						comparison: 'eq',
						value: id
					}))
				});
			} else {
				if (menu.data.categoryFilter?.length) {
					let path = website === Constants.WEB_OXS
							? '/data/countrySpecific/' + country + '/categories'
							: '/data/rdbCategories';
					filter.$and.push({
						$or: menu.data.categoryFilter.map(value => ({
							field: path,
							comparison: 'eq',
							value,
						}))
					});
				}
			}
			if (menu.data.materialTypes?.length) {
				let matFilter: Search.SearchFilter = {
					$or: menu.data.materialTypes.map((id) => ({
						field: '/data/typeOfMaterialRestrictions',
						comparison: 'eq',
						value: id,
					})),
				};
				matFilter.$or.push({
					$not: {
						field: '/data',
						comparison: 'exists',
						value: 'typeOfMaterialRestrictions',
					},
				});
				filter.$and.push(matFilter);
			}
			if (menu.data.search.species?.length) {
				filter.$and.push({
					$or: menu.data.search.species.map((id) => ({
						field: '/id',
						comparison: 'eq',
						value: id,
					})),
				});
			}
			let children = this.getChildren(menu.id);
			let childFilters = children.map((c) => this.getSpeciesFilter(c, forRequest));
			return { $or: childFilters.concat(filter) };
		},
		getCombinedMenuItem(menu: Ent): Ent {
			menu = JSON.parse(JSON.stringify(menu));
			if (!menu.data.availableRequestTypes) menu.data.availableRequestTypes = [];
			if (!menu.data.materialTypes) menu.data.materialTypes = [];
			let children = this.getChildren(menu.id);
			for (let child of children) {
				child.data.availableRequestTypes?.forEach((t) => {
					if (!menu.data.availableRequestTypes!.includes(t)) {
						menu.data.availableRequestTypes!.push(t);
					}
				});
				child.data.materialTypes?.forEach((t) => {
					if (!menu.data.materialTypes!.includes(t)) {
						menu.data.materialTypes!.push(t);
					}
				});
				menu.data.search.showAvailableFilter =
					menu.data.search.showAvailableFilter || child.data.search.showAvailableFilter;
				menu.data.search.showSortFilter =
					menu.data.search.showSortFilter || child.data.search.showSortFilter;
				menu.data.search.showPrmStatusFilter =
					menu.data.search.showPrmStatusFilter || child.data.search.showPrmStatusFilter;
				menu.data.search.showHistoryFilter =
					menu.data.search.showHistoryFilter || child.data.search.showHistoryFilter;
			}
			return menu;
		},
	},
});
